import React, { useEffect, useRef, useState} from "react";
import "./styles.css";
import "froala-editor/js/plugins.pkgd.min.js";
import { uploadMedia } from "../../apiCall";
import {
  controlledLanguageAuthoringContent,
  simplifyAuthoringContent,
  grammarAuthoringContent,
  draftAuthoringContent,
  expandAuthoringContent,
} from "../../../services/aiService";
import { getSelectedApprovedDocuments } from "../../AIAssistant/ApprovedSources";
import { Loader, Modal, Icon } from "semantic-ui-react";
import {useHistory} from 'react-router-dom';
// import "froala-editor/js/froala_editor.pkgd.min.js";

// Require Editor CSS files.
// import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import Froalaeditor from "froala-editor";
import Froala from "react-froala-wysiwyg";
import { connect } from "react-redux";
import { aiButtonon } from "../../../redux/actions";

// Draggable AI Warning Badge Component
const DraggableAiWarningBadge = ({ visible, text, containerRef }) => {
  const [position, setPosition] = useState({ x: null, y: null }); // null = auto-calculate
  const [isDragging, setIsDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const badgeRef = useRef(null);
  const dragStateRef = useRef({ startX: 0, startY: 0, offsetX: 0, offsetY: 0 });

  // Calculate initial position (bottom-right corner)
  useEffect(() => {
    if (visible && containerRef?.current && badgeRef.current && position.x === null) {
      const container = containerRef.current.getBoundingClientRect();
      const badge = badgeRef.current.getBoundingClientRect();
      
      // Position at bottom-right with some padding
      setPosition({
        x: container.width - badge.width - 20,
        y: container.height - badge.height - 80, // Leave space for footer buttons
      });
    }
  }, [visible, containerRef, position.x]);

  // Reset position when modal opens
  useEffect(() => {
    if (visible) {
      setPosition({ x: null, y: null });
      setIsMinimized(false);
    }
  }, [visible]);

  const clampPosition = (x, y) => {
    if (!containerRef?.current || !badgeRef.current) {
      return { x, y };
    }

    const container = containerRef.current.getBoundingClientRect();
    const badge = badgeRef.current.getBoundingClientRect();

    const maxX = container.width - badge.width - 10;
    const maxY = container.height - badge.height - 10;

    return {
      x: Math.max(10, Math.min(x, maxX)),
      y: Math.max(10, Math.min(y, maxY)),
    };
  };

  const handlePointerDown = (e) => {
    // Don't start drag if clicking minimize button
    if (e.target.closest('.badge-minimize-btn')) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    if (!badgeRef.current || !containerRef?.current) return;

    const badge = badgeRef.current.getBoundingClientRect();
    const container = containerRef.current.getBoundingClientRect();

    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - badge.left + container.left,
      offsetY: e.clientY - badge.top + container.top,
    };

    setIsDragging(true);
    badgeRef.current.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging || !containerRef?.current) return;

    e.preventDefault();
    const container = containerRef.current.getBoundingClientRect();
    
    const newX = e.clientX - container.left - dragStateRef.current.offsetX;
    const newY = e.clientY - container.top - dragStateRef.current.offsetY;

    const clamped = clampPosition(newX, newY);
    setPosition(clamped);
  };

  const handlePointerUp = (e) => {
    if (isDragging) {
      e.preventDefault();
      setIsDragging(false);
      if (badgeRef.current) {
        badgeRef.current.releasePointerCapture(e.pointerId);
      }
    }
  };

  const toggleMinimize = (e) => {
    e.stopPropagation();
    setIsMinimized(!isMinimized);
  };

  if (!visible || position.x === null) return null;

  return (
    <div
      ref={badgeRef}
      className={`draggable-ai-warning-badge ${isDragging ? 'dragging' : ''} ${isMinimized ? 'minimized' : ''}`}
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 1000,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      role="alert"
      aria-label="AI generated content is not source verified - draggable badge"
      title="Drag to reposition"
    >
      <div className="badge-content">
        <Icon name="warning circle" className="badge-icon" />
        {!isMinimized && (
          <>
            <span className="badge-text">{text}</span>
            <button 
              className="badge-minimize-btn"
              onClick={toggleMinimize}
              aria-label="Minimize badge"
              title="Minimize"
            >
              <Icon name="minus" />
            </button>
          </>
        )}
        {isMinimized && (
          <button 
            className="badge-minimize-btn"
            onClick={toggleMinimize}
            aria-label="Expand badge"
            title="Expand"
          >
            <Icon name="plus" />
          </button>
        )}
      </div>
      <div className="drag-indicator" title="Drag to move">
        <Icon name="move" />
      </div>
    </div>
  );
};


const FroalaEditor = (props) => {
  const history = useHistory();
  const [fileCount, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  let imageFiles = [];
  const editorRef = useRef(null);
  const inlineToolbarRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const isApplyingAIRef = useRef(false);
  const lastPropagatedContentRef = useRef(props.content || "");
  const hoveredBlockRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const highlightTimersRef = useRef(new Map());
  const pointerRef = useRef({ overEditor: false, overToolbar: false, overTextBlock: false });
  const modalBodyRef = useRef(null); // Ref for modal body as drag boundary
  const draftAbortControllerRef = useRef(null);
  const draftTimeoutRef = useRef(null);
  const draftInFlightRef = useRef(false);
  const [defaultContent, setDefaultContent] = useState(
    props.content.length > 0 ? props.content : ""
  );
  const [inlineAiLoading, setInlineAiLoading] = useState(false);
  const [isDrafting, setIsDrafting] = useState(false);
  const [inlineAiAction, setInlineAiAction] = useState("");
  const [lastFailedAiAction, setLastFailedAiAction] = useState("");
  const [inlineAiError, setInlineAiError] = useState("");
  const [aiReviewModal, setAiReviewModal] = useState({
    open: false,
    action: "",
    originalText: "",
    suggestedText: "",
    responseContent: "",
    responseTitle: "",
    structuredSuggestion: null,
    imageMap: new Map(),
    groundingData: null, // sources_used, warnings, needs_review, input_conflicting_claims, etc.
  });
  const [inlineAi, setInlineAi] = useState({
    toolbarVisible: false,
    top: 0,
    left: 0,
    blockText: "",
    positionBelow: true,
  });

  const INLINE_AI_ACTIONS = [
    { key: "rewrite", label: "Rewrite" },
    { key: "simplify", label: "Simplify" },
    { key: "grammar", label: "Grammar" },
    { key: "draft", label: "Draft" },
    { key: "expand", label: "Expand" },
  ];

  const ACTION_REVIEW_TITLES = {
    rewrite: "Rewrite to Controlled Language",
    simplify: "Simplify with AI",
    grammar: "Grammar Correction Review",
    draft: "Draft Generation Review",
    expand: "Expanded Content Review",
  };

  const HOVER_DELAY_MS = 250;
  const HIDE_DELAY_MS = 140;
  const CHANGE_HIGHLIGHT_MS = 4500;
  const INLINE_TOOLBAR_WIDTH = 560;
  const INLINE_TOOLBAR_HEIGHT = 52;
  const INLINE_TOOLBAR_GAP = 12;
  const MAX_CONTENT_LENGTH = 8000;

  const clearTimers = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    highlightTimersRef.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    highlightTimersRef.current.clear();
  };

  const emitContentChange = (content) => {
    const normalizedContent = String(content || "");
    if (lastPropagatedContentRef.current === normalizedContent) {
      return;
    }

    lastPropagatedContentRef.current = normalizedContent;

    if (typeof props.getContent === "function") {
      console.count("FroalaEditor.emitContentChange");
      props.getContent(normalizedContent);
    }
  };

  const applyChangeHighlight = (block, editor) => {
    if (!block?.classList || !editor) return;

    const markerId = `inline-ai-change-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    block.classList.add("inline-ai-changed-block");
    block.setAttribute("data-inline-ai-change-id", markerId);

    const existingTimer = highlightTimersRef.current.get(markerId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timeoutId = setTimeout(() => {
      const currentEditor = editorInstanceRef.current;
      const target = currentEditor?.el?.querySelector(
        `[data-inline-ai-change-id="${markerId}"]`
      );

      if (target) {
        target.classList.remove("inline-ai-changed-block");
        target.removeAttribute("data-inline-ai-change-id");
        const cleanedContent = currentEditor.html.get();
        setDefaultContent(cleanedContent);
        emitContentChange(cleanedContent);
      }

      highlightTimersRef.current.delete(markerId);
    }, CHANGE_HIGHLIGHT_MS);

    highlightTimersRef.current.set(markerId, timeoutId);
  };

  const closeAiReviewModal = () => {
    setAiReviewModal({
      open: false,
      action: "",
      originalText: "",
      suggestedText: "",
      responseContent: "",
      responseTitle: "",
      structuredSuggestion: null,
      imageMap: new Map(),
      groundingData: null,
    });
  };

  const applyReviewedSuggestion = () => {
    const editor = editorInstanceRef.current;
    const block = hoveredBlockRef.current;

    const suggestedText = String(aiReviewModal.suggestedText || "").trim();
    const action = aiReviewModal.action;

    if (!editor || !suggestedText) {
      closeAiReviewModal();
      return;
    }

    editor.events.focus(true);

    // Restore image placeholders that were tokenized before the AI call.
    const htmlWithImages = detokenizeImages(
      suggestedText,
      aiReviewModal.imageMap
    );

    // Prefer structured legacy response only when no inline content was returned.
    const hasStructuredFallback =
      (action === "draft" || action === "expand") &&
      aiReviewModal.structuredSuggestion &&
      !aiReviewModal.responseContent;

    if (hasStructuredFallback) {
      const structured = aiReviewModal.structuredSuggestion;
      const htmlParts = [];

      if (structured.procedureTitle) {
        htmlParts.push(`<h3>${structured.procedureTitle}</h3>`);
      }

      if (structured.procedureDescription) {
        htmlParts.push(`<p>${structured.procedureDescription}</p>`);
      }

      if (structured.steps?.length > 0) {
        htmlParts.push(`<p><strong>Steps:</strong></p>`);
        htmlParts.push(`<ol>`);
        structured.steps.forEach((step) => {
          const stepTitle = step?.stepTitle
            ? `<strong>${step.stepTitle}:</strong> `
            : "";
          const stepDesc = step?.stepDescription || "";
          htmlParts.push(`<li>${stepTitle}${stepDesc}</li>`);
        });
        htmlParts.push(`</ol>`);
      }

      const htmlContent = htmlParts.join("");

      if (block) {
        editor.selection.setAtEnd(block);
        editor.selection.save();
        block.innerHTML = "";
        editor.html.insert(htmlContent);
        editor.selection.restore();
      } else {
        editor.html.insert(htmlContent);
      }
    } else {
      // In-place replacement for all actions.
      const htmlText = htmlWithImages.replace(/\n/g, "<br>");

      if (block) {
        block.innerHTML = htmlText;
        applyChangeHighlight(block, editor);
      } else {
        editor.html.insert(`<p>${htmlText}</p>`);
      }
    }

    // Auto-fill step title when the backend returns one.
    if (
      aiReviewModal.responseTitle &&
      typeof props.onStepTitleChange === "function"
    ) {
      props.onStepTitleChange(aiReviewModal.responseTitle);
    }

    // Update editor state
    const updatedContent = editor.html.get();
    setDefaultContent(updatedContent);
    emitContentChange(updatedContent);

    closeAiReviewModal();
  };

  // Extract content/title from different X-Gen response shapes.
  const resolveResponseContent = (response) => {
    if (!response || typeof response !== "object") {
      return { content: "", title: "" };
    }
    const data = response.data || response;
    const content =
      data?.content ||
      data?.data?.content ||
      data?.result ||
      data?.rewritten ||
      data?.text ||
      data?.output ||
      data?.procedureDescription ||
      data?.transformed_text ||
      data?.rewritten_text ||
      data?.controlled_language_text ||
      data?.suggestion ||
      data?.revised_text ||
      "";
    const title =
      data?.title ||
      data?.data?.title ||
      data?.procedureTitle ||
      data?.stepTitle ||
      data?.rewritten_title ||
      "";
    return { content, title };
  };

  // Preserve image nodes across AI transformations by replacing them with
  // unique tokens before sending and swapping them back into the response.
  const tokenizeImages = (html) => {
    if (!html) return { text: "", imageMap: new Map() };
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const imageMap = new Map();

    doc.querySelectorAll("img").forEach((img, index) => {
      const id =
        img.getAttribute("id") ||
        img.getAttribute("data-id") ||
        `img-${index}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const token = `[[IMAGE:${id}]]`;
      imageMap.set(token, img.outerHTML);
      img.replaceWith(doc.createTextNode(token));
    });

    return {
      text: doc.body.innerText || "",
      imageMap,
    };
  };

  const detokenizeImages = (text, imageMap) => {
    if (!text || !imageMap?.size) return text;
    let result = String(text);
    imageMap.forEach((html, token) => {
      result = result.split(token).join(html);
    });
    return result;
  };

  const getTextBlockElement = (target, editor) => {
    if (!target || !editor?.el) return null;
    const node = target.nodeType === window.Node.TEXT_NODE ? target.parentElement : target;
    if (!node || !editor.el.contains(node)) return null;

    return node.closest("p, li, blockquote, td, th, h1, h2, h3, h4, h5, h6, pre");
  };

  const getBlockText = (block) => String(block?.innerText || "").replace(/\s+/g, " ").trim();

  const showToolbarForBlock = (block) => {
    const blockText = getBlockText(block);
    if (!blockText) return;

    const rect = block.getBoundingClientRect();
    const editor = editorInstanceRef.current;
    const editorRect = editor?.el?.getBoundingClientRect();
    
    // Determine if popup should appear below or above
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const positionBelow = spaceBelow >= INLINE_TOOLBAR_HEIGHT + INLINE_TOOLBAR_GAP + 20 || spaceBelow > spaceAbove;
    
    // Calculate horizontal position (centered on block)
    let left = rect.left + (rect.width / 2);
    
    // Calculate vertical position
    let top;
    if (positionBelow) {
      top = rect.bottom + INLINE_TOOLBAR_GAP;
    } else {
      top = rect.top - INLINE_TOOLBAR_HEIGHT - INLINE_TOOLBAR_GAP;
    }
    
    hoveredBlockRef.current = block;
    setInlineAi((prev) => ({
      ...prev,
      toolbarVisible: true,
      top,
      left,
      blockText,
      positionBelow,
    }));
    setInlineAiError("");
  };

  const hideInlineAi = () => {
    hoveredBlockRef.current = null;
    setInlineAi((prev) => ({
      ...prev,
      toolbarVisible: false,
      blockText: "",
    }));
  };

  const scheduleHideIfNeeded = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = setTimeout(() => {
      if (
        pointerRef.current.overTextBlock ||
        pointerRef.current.overToolbar ||
        inlineAiLoading
      ) {
        return;
      }
      hideInlineAi();
    }, HIDE_DELAY_MS);
  };

  const readStructuredEnhanceResult = (response) => {
    const candidates = [
      response?.raw,
      response,
    ];

    const source = candidates.find(
      (item) =>
        item &&
        typeof item === "object" &&
        (item.procedureTitle || item.procedureDescription || Array.isArray(item.steps))
    );

    if (!source) return null;

    return {
      procedureTitle: String(source.procedureTitle || "").trim(),
      procedureDescription: String(source.procedureDescription || "").trim(),
      steps: Array.isArray(source.steps) ? source.steps : [],
    };
  };

  const formatStructuredSuggestionText = (structured) => {
    if (!structured) return "";

    const lines = [];
    if (structured.procedureTitle) {
      lines.push(structured.procedureTitle);
    }
    if (structured.procedureDescription) {
      lines.push(structured.procedureDescription);
    }
    if (structured.steps?.length) {
      lines.push("Steps:");
      structured.steps.forEach((step, index) => {
        const title = step?.stepTitle ? `${step.stepTitle}: ` : "";
        const desc = step?.stepDescription || "";
        lines.push(`${index + 1}. ${title}${desc}`.trim());
      });
    }

    return lines.join("\n\n").trim();
  };

  const handleEditorMouseMove = (event, editor) => {
    if (!props.formdesc) return;
    pointerRef.current.overEditor = !!editor?.el?.contains(event?.target);
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    const block = getTextBlockElement(event?.target, editor);

    if (!block) {
      pointerRef.current.overTextBlock = false;
      scheduleHideIfNeeded();
      return;
    }

    pointerRef.current.overTextBlock = true;

    const sameBlock = hoveredBlockRef.current === block;

    if (sameBlock) {
      // Update position if already visible for the same block
      if (inlineAi.toolbarVisible) {
        const rect = block.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const positionBelow = spaceBelow >= INLINE_TOOLBAR_HEIGHT + INLINE_TOOLBAR_GAP + 20 || spaceBelow > spaceAbove;
        
        let left = rect.left + (rect.width / 2);
        let top;
        if (positionBelow) {
          top = rect.bottom + INLINE_TOOLBAR_GAP;
        } else {
          top = rect.top - INLINE_TOOLBAR_HEIGHT - INLINE_TOOLBAR_GAP;
        }
        
        setInlineAi((prev) => ({
          ...prev,
          top,
          left,
          blockText: getBlockText(block),
          positionBelow,
        }));
      }
      return;
    }

    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    hoverTimerRef.current = setTimeout(() => {
      showToolbarForBlock(block);
    }, HOVER_DELAY_MS);
  };

  const handleEditorMouseLeave = () => {
    pointerRef.current.overEditor = false;
    pointerRef.current.overTextBlock = false;
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    scheduleHideIfNeeded();
  };

  const handleEditorMouseEnter = () => {
    pointerRef.current.overEditor = true;
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const handleInlineAiAction = async (action) => {
    const isDraftAction = action === "draft";
    if ((isDraftAction && (isDrafting || draftInFlightRef.current)) || inlineAiLoading) {
      return;
    }

    const editor = editorInstanceRef.current;
    const block = hoveredBlockRef.current;

    if (!editor || !block) {
      hideInlineAi();
      return;
    }

    // Tokenize images so they survive as placeholders in the plain-text payload.
    const { text: tokenizedText, imageMap } = tokenizeImages(block.innerHTML);
    const selectedText = tokenizedText || String(inlineAi.blockText || "").trim();

    if (!selectedText) {
      hideInlineAi();
      return;
    }

    try {
      setInlineAiAction(action);
      setInlineAiError("");
      if (isDraftAction) {
        setIsDrafting(true);
        draftInFlightRef.current = true;
      } else {
        setInlineAiLoading(true);
      }
      setLastFailedAiAction("");

      let response;
      const category = "Setup Instruction";

      // Get approved documents for Draft/Expand actions
      const approvedDocuments =
        action === "draft" || action === "expand"
          ? getSelectedApprovedDocuments()
          : [];

      // Call the appropriate API based on action
      switch (action) {
        case "rewrite":
          // UAT Step 12: "Rewrite to Controlled Language" must call /controlled-language.
          response = await controlledLanguageAuthoringContent({
            content: selectedText,
            category,
          });
          break;
        case "simplify":
          response = await simplifyAuthoringContent({
            content: selectedText,
            category,
          });
          break;
        case "grammar":
          response = await grammarAuthoringContent({
            content: selectedText,
            category,
          });
          break;
        case "draft":
          if (draftAbortControllerRef.current) {
            draftAbortControllerRef.current.abort();
          }

          draftAbortControllerRef.current = new AbortController();
          if (draftTimeoutRef.current) {
            clearTimeout(draftTimeoutRef.current);
          }

          draftTimeoutRef.current = setTimeout(() => {
            if (draftAbortControllerRef.current) {
              draftAbortControllerRef.current.abort();
            }
          }, 30000);

          response = await draftAuthoringContent({
            user_prompt: selectedText,
            category,
            approved_documents: approvedDocuments,
            signal: draftAbortControllerRef.current.signal,
            timeoutMs: 30000,
          });

          if (draftTimeoutRef.current) {
            clearTimeout(draftTimeoutRef.current);
            draftTimeoutRef.current = null;
          }
          draftAbortControllerRef.current = null;
          break;
        case "expand":
          response = await expandAuthoringContent({
            content: selectedText,
            category,
            detail_level: "moderate",
            approved_documents: approvedDocuments,
          });
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      // New contract: editing endpoints return { content, title }.
      console.log("[handleInlineAiAction] response:", response);
      const { content: rawContent, title: rawTitle } = resolveResponseContent(response);
      const responseContent = String(rawContent || "").trim();
      const responseTitle = String(rawTitle || "").trim();
      let enhancedText = responseContent;
      let structuredSuggestion = null;

      // Legacy fallback for Draft/Expand structured payloads.
      const isStructuredDraftOrExpand =
        (action === "draft" || action === "expand") &&
        (response?.procedureDescription || Array.isArray(response?.steps));

      if (!enhancedText && isStructuredDraftOrExpand) {
        structuredSuggestion = {
          procedureTitle: response?.procedureTitle || "",
          procedureDescription: response?.procedureDescription || "",
          steps: Array.isArray(response?.steps) ? response.steps : [],
        };
        enhancedText = formatStructuredSuggestionText(structuredSuggestion);
      }

      if (!enhancedText) {
        throw new Error("AI did not return enhanced content.");
      }

      if (ACTION_REVIEW_TITLES[action]) {
        // Grounding metadata contract:
        // - rewrite/simplify/grammar no longer return grounding_score, needs_review, unsupported_claims.
        // - draft/expand return needs_review, input_conflicting_claims, sources_used, is_general_ai.
        const isGroundedAction = action === "draft" || action === "expand";
        const groundingData = {
          sources_used: response?.sources_used || [],
          warnings: response?.warnings || [],
          missing_details: response?.missing_details || [],
          is_general_ai: response?.is_general_ai || false,
          needs_review: isGroundedAction ? response?.needs_review || false : false,
          input_conflicting_claims: isGroundedAction
            ? response?.input_conflicting_claims || []
            : [],
        };

        setAiReviewModal({
          open: true,
          action,
          originalText: selectedText,
          suggestedText: enhancedText,
          responseContent,
          responseTitle,
          structuredSuggestion,
          imageMap,
          groundingData,
        });
      } else {
        editor.events.focus(true);
        block.innerHTML = detokenizeImages(enhancedText, imageMap);
        applyChangeHighlight(block, editor);

        const updatedContent = editor.html.get();
        setDefaultContent(updatedContent);
        emitContentChange(updatedContent);
        showToolbarForBlock(block);
      }
    } catch (error) {
      let errorMessage = error?.userMessage || "Unable to process selected text right now.";

      const status = error?.response?.status;
      const errorCode = error?.response?.data?.code || error?.code;
      const errorMsg =
        error?.response?.data?.msg ||
        error?.response?.data?.detail ||
        error?.message;

      if (error?.code === "ERR_CANCELED") {
        errorMessage = "Draft request timed out after 30 seconds. Please try again.";
      } else if (
        errorCode === "APPROVED_SOURCE_EVIDENCE_NOT_FOUND" ||
        errorMsg?.includes("APPROVED_SOURCE_EVIDENCE_NOT_FOUND")
      ) {
        errorMessage =
          "No relevant approved or ingested source was found for this draft.";
      } else if (
        errorCode === "EXPANSION_TOO_LONG" ||
        errorMsg?.includes("EXPANSION_TOO_LONG")
      ) {
        errorMessage =
          "The expansion exceeds the allowed length. Try selecting less text or a lower detail level.";
      } else if (
        errorCode === "TRANSFORMATION_INTEGRITY_VIOLATION" ||
        errorMsg?.includes("TRANSFORMATION_INTEGRITY_VIOLATION")
      ) {
        errorMessage =
          "The selected content changed during processing. Please try again without modifying images or formatting.";
      } else if (
        errorCode === "GROUNDING_VALIDATION_FAILED" ||
        errorMsg?.includes("GROUNDING_VALIDATION_FAILED")
      ) {
        errorMessage =
          "Could not validate content against approved sources. Please try again or select different sources.";
      } else if (errorMsg) {
        errorMessage = errorMsg;
      }

      setLastFailedAiAction(action);
      setInlineAiError(errorMessage);
    } finally {
      if (draftTimeoutRef.current) {
        clearTimeout(draftTimeoutRef.current);
        draftTimeoutRef.current = null;
      }
      draftAbortControllerRef.current = null;
      draftInFlightRef.current = false;
      setInlineAiLoading(false);
      setIsDrafting(false);
      setInlineAiAction("");
    }
  };
 
  // Removed custom Froala "Generate with LexX" image button.
  // The dedicated top-right "Enhance with AI" button remains the entry point.
  if (!Froalaeditor.COMMANDS?.["reconstruct-ai-button"]) {
    Froalaeditor.DefineIcon("reconstruct-ai-button", { NAME: "magic" });
    Froalaeditor.RegisterCommand("reconstruct-ai-button", {
      title: "Reconstruct with AI",
      focus: false,
      undo: false,
      refreshAfterCallback: false,
      callback: function () {
        if (props.aiProcessing) return;
        if (typeof props.onReconstructWithAI === "function") {
          props.onReconstructWithAI();
        }
      },
    });
  }
  useEffect(() => {
      const incomingContent = props.content === "" ? "" : String(props.content || "");
      lastPropagatedContentRef.current = incomingContent;
      setDefaultContent(incomingContent);
  }, [props.content]);

  useEffect(() => {
    if (!props.formdesc) return undefined;

    const handleDocumentMouseDown = (event) => {
      const isInsideToolbar = inlineToolbarRef.current?.contains(event.target);
      if (isInsideToolbar) return;
      scheduleHideIfNeeded();
    };

    const handleScrollResize = () => {
      if (inlineAi.toolbarVisible && hoveredBlockRef.current) {
        const block = hoveredBlockRef.current;
        const rect = block.getBoundingClientRect();
        
        // Check if block is still visible
        if (rect.top < 0 || rect.bottom > window.innerHeight || rect.left < 0 || rect.right > window.innerWidth) {
          hideInlineAi();
          return;
        }
        
        // Recalculate position
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const positionBelow = spaceBelow >= INLINE_TOOLBAR_HEIGHT + INLINE_TOOLBAR_GAP + 20 || spaceBelow > spaceAbove;
        
        let left = rect.left + (rect.width / 2);
        let top;
        if (positionBelow) {
          top = rect.bottom + INLINE_TOOLBAR_GAP;
        } else {
          top = rect.top - INLINE_TOOLBAR_HEIGHT - INLINE_TOOLBAR_GAP;
        }
        
        setInlineAi((prev) => ({
          ...prev,
          top,
          left,
          positionBelow,
        }));
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && inlineAi.toolbarVisible) {
        hideInlineAi();
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    window.addEventListener("scroll", handleScrollResize, true);
    window.addEventListener("resize", handleScrollResize);
    document.addEventListener("keydown", handleKeyDown);
    
    return () => {
      clearTimers();
      document.removeEventListener("mousedown", handleDocumentMouseDown);
      window.removeEventListener("scroll", handleScrollResize, true);
      window.removeEventListener("resize", handleScrollResize);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [props.formdesc, inlineAi.toolbarVisible]);

  useEffect(() => {
    return () => {
      if (draftTimeoutRef.current) {
        clearTimeout(draftTimeoutRef.current);
        draftTimeoutRef.current = null;
      }

      if (draftAbortControllerRef.current) {
        draftAbortControllerRef.current.abort();
        draftAbortControllerRef.current = null;
      }

      draftInFlightRef.current = false;
    };
  }, []);

  // Handle AI Assistant action suggestions from sidebar
  useEffect(() => {
    const handleAIActionSuggestion = (event) => {
      console.count("FroalaEditor.handleAIActionSuggestion");
      const { content, action } = event.detail || {};
      
      if (!content) return;

      const editor = editorInstanceRef.current;
      if (!editor) {
        console.warn("Froala editor not initialized");
        return;
      }

      // Convert plain text to HTML with proper formatting
      const lines = content.split('\n');
      const htmlParts = [];

      lines.forEach(line => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;

        // Check if line starts with a number (numbered list item)
        const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)/);
        if (numberedMatch) {
          const [, number, text] = numberedMatch;
          htmlParts.push(`<p><strong>${number}. ${text.split(':')[0]}</strong></p>`);
          const restOfText = text.includes(':') ? text.split(':').slice(1).join(':').trim() : '';
          if (restOfText) {
            htmlParts.push(`<p>${restOfText}</p>`);
          }
        } else {
          // Regular paragraph
          htmlParts.push(`<p>${trimmedLine}</p>`);
        }
      });

      const htmlContent = htmlParts.join('');

      const currentHtml = String(editor.html.get() || "");
      if (currentHtml === htmlContent) {
        return;
      }

      // Update editor with AI suggestion
      isApplyingAIRef.current = true;
      editor.events.focus(true);
      editor.html.set(htmlContent);
      
      // Update state and notify parent component
      setDefaultContent(htmlContent);
      emitContentChange(htmlContent);

      requestAnimationFrame(() => {
        isApplyingAIRef.current = false;
      });

      console.log("AI suggestion applied:", action);
    };

    window.addEventListener("ai-assistant:apply-action-suggestion", handleAIActionSuggestion);

    return () => {
      window.removeEventListener("ai-assistant:apply-action-suggestion", handleAIActionSuggestion);
    };
  }, [props.getContent]);

  const config = {
    key: "BWC6D-16E3F3B2H3E1A6A1A9XhC-11blFe1a1PVWEc1Fd1XHTHc1THMMe1NCb1tA1A1A1D1H4A1C2B1C7D6==",
    wordPasteModal: false,
    wordPasteKeepFormatting: false,
    attribution: false,
    fileUpload: false,
    heightMin: 200,
    heightMax: 300,
    widthMin:100,
    tabSpaces: 5,
    //Image parameters
    imageUpload: true,
    quickInsertEnabled: false,
    imageDefaultAlign: "left",
    linkText: false,
    linkAlwaysBlank: true,
    imageDefaultDisplay: "block", 
    imageInsertButtons: ["imageBack", "|", "imageUpload", "imageByURL"],
    // Set max image size to 100MB.
    imageMaxSize: 100 * 1024 * 1024,
    // Allow to upload PNG and JPG.
    imageAllowedTypes: ["jpeg", "jpg", "png"],
    // Video Parameteres
    videoAllowedTypes: ["mp4", "webm", "ogg", "mov"],
    videoMaxSize: 1024 * 1024 * 100,
    videoUpload: true,
    videoInsertButtons: ["videoBack", "|", "videoByURL"],
    events: {
      initialized: function () {
        editorInstanceRef.current = this;
      },
      "image.inserted": async function (img, response) {
        let editor = this;
        imageFiles.push(img);
        setCount(fileCount + 1);

        
          setLoading(true);
          let src = img.attr("src");
          let srcSplit = src.split("/");

          await fetch(src)
            .then((res) => res.blob())
            .then(async (blob) => {
              const currentImage = img;
              const file = new File(
                [blob],
                srcSplit[srcSplit.length - 1],
                blob
              );
 
              const data = new FormData();
              data.append("media", file);
              await uploadMedia(data)
                .then(async (res) => {
                  currentImage.attr("src", res.data.data.url);
                  img = currentImage;

 
                  var htmlContent = editor.html.get();
                  var updatedContent = htmlContent.replace(
                    src,
                    res.data.data.url
                  );
                  editor.html.set(updatedContent);
                  props.getContent(updatedContent);
                })
                .catch((err) => {
                  console.log("err", img, err);
                });
            });
        
       
        setLoading(false);
      },
      mousemove: function (event) {
        handleEditorMouseMove(event, this);
      },
      mouseenter: function () {
        handleEditorMouseEnter();
      },
      mouseleave: function () {
        handleEditorMouseLeave();
      },
      blur: function () {
        handleEditorMouseLeave();
      },
    },
    imageEditButtons: [
      "imageCaption",
      "imageReplace",
      "imageAlign",
      "imageRemove",
      "|",
      "imageLink",
      "linkOpen",
      "linkEdit",
      "linkRemove",
      "-",
      "imageDisplay",
      "imageStyle",
      
    ],
    toolbarButtons: {
      moreText: {
        buttons: [
          "bold",
          "italic",
          "underline",
          "strikeThrough",
          "subscript",
          "superscript",
          "fontFamily",
          "fontSize",
          "textColor",
          "backgroundColor",
          "inlineClass",
          "inlineStyle",
          "clearFormatting",
         
        ],
      },

      moreParagraph: {
        buttons: [
          "alignLeft",
          "alignCenter",
          "alignRight",
          "alignJustify",

          //   "formatOLSimple",
          "formatOL",
          "formatUL",
          "paragraphFormat",
          "lineHeight",
          "outdent",
          "indent",
          "quote",
        ],
      },

      moreRich: {
        buttons: [
          "insertTable",
          "insertLink",
          "insertImage",
          "insertVideo",

          "emoticons",
          "fontAwesome",
          "specialCharacters",
          "embedly",
          "insertHR",
        ],
      },
      moreMisc: {
        buttons: [
          props.enableReconstructAI !== false && "reconstruct-ai-button",
          "undo",
          "redo",
          "fullscreen",
          "spellChecker",
          "selectAll",
          "help",
        ].filter(Boolean),
        align: "right",
        buttonsVisible: 8,
      },
    },
  };

  const onModelChange = (model) => {
    console.count("FroalaEditor.onModelChange");

    if (isApplyingAIRef.current) {
      console.count("FroalaEditor.onModelChange.skippedApplyingAI");
      return;
    }

    const nextContent = String(model || "");

    if (
      nextContent === defaultContent &&
      nextContent === lastPropagatedContentRef.current
    ) {
      return;
    }

    setDefaultContent((previousContent) =>
      previousContent === nextContent ? previousContent : nextContent
    );
    emitContentChange(nextContent);
  };

  const getInlineToolbarStyle = () => {
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
    
    // Center horizontally on the anchor point
    let left = inlineAi.left - (INLINE_TOOLBAR_WIDTH / 2);
    
    // Ensure popup doesn't go off the right edge
    const maxLeft = viewportWidth - INLINE_TOOLBAR_WIDTH - 16;
    left = Math.min(left, maxLeft);
    
    // Ensure popup doesn't go off the left edge
    left = Math.max(16, left);
    
    // Vertical position already calculated in showToolbarForBlock
    let top = inlineAi.top;
    
    // Final safety check for top boundary
    top = Math.max(8, top);
    top = Math.min(top, viewportHeight - INLINE_TOOLBAR_HEIGHT - 8);

    return { 
      top: `${top}px`, 
      left: `${left}px`,
      transform: 'none'
    };
  };

  return (
    <div className="EditorComponent">
      <Modal open={loading}>
        <Loader active>Image is processing...</Loader>
      </Modal>

      <Froala
        ref={editorRef}
        model={defaultContent}
        onModelChange={(e) => onModelChange(e)}
        // tag="textarea"
        config={config}
      ></Froala>

      {props.formdesc && inlineAi.toolbarVisible && !aiReviewModal.open && (
        <div
          className="inline-ai-toolbar-modern"
          ref={inlineToolbarRef}
          style={getInlineToolbarStyle()}
          onMouseEnter={() => {
            pointerRef.current.overToolbar = true;
            if (hideTimerRef.current) {
              clearTimeout(hideTimerRef.current);
              hideTimerRef.current = null;
            }
          }}
          onMouseLeave={() => {
            pointerRef.current.overToolbar = false;
            scheduleHideIfNeeded();
          }}
          role="toolbar"
          aria-label="AI Writing Actions"
        >
          {/* Caret/Arrow pointing to selected text */}
          <div className={`inline-ai-caret ${inlineAi.positionBelow ? 'caret-top' : 'caret-bottom'}`} />
          
          {/* AI Header */}
          <div className="inline-ai-header">
            <Icon name="magic" className="ai-sparkle-icon" />
            <span className="ai-header-text">AI Actions</span>
          </div>
          
          <div className="inline-ai-divider" />
          
          {/* Character limit check */}
          {inlineAi.blockText.length > MAX_CONTENT_LENGTH ? (
            <div className="inline-ai-char-limit-warning">
              <Icon name="warning sign" />
              <span>Selection too long. Max {MAX_CONTENT_LENGTH.toLocaleString()} characters.</span>
            </div>
          ) : (inlineAiLoading && inlineAiAction !== "draft") ? (
            <div className="inline-ai-loading-state">
              <Icon loading name="spinner" />
              <span>
                {inlineAiAction === "rewrite" && "AI is rewriting your content..."}
                {inlineAiAction === "simplify" && "AI is simplifying your content..."}
                {inlineAiAction === "grammar" && "Checking grammar..."}
                {inlineAiAction === "draft" && "Generating draft..."}
                {inlineAiAction === "expand" && "Expanding content..."}
              </span>
            </div>
          ) : inlineAiError ? (
            <div className="inline-ai-error-state">
              <Icon name="warning circle" />
              <span>{inlineAiError}</span>
              <button 
                className="inline-ai-retry-btn" 
                onClick={() => {
                  setInlineAiError("");
                  const actionToRetry = lastFailedAiAction || inlineAiAction;
                  if (actionToRetry) {
                    handleInlineAiAction(actionToRetry);
                  }
                }}
                type="button"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="inline-ai-actions">
              {INLINE_AI_ACTIONS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="inline-ai-action-btn"
                  onClick={() => handleInlineAiAction(item.key)}
                  disabled={inlineAiLoading || isDrafting}
                  aria-label={`${item.label} with AI`}
                  title={`${item.label} with AI`}
                >
                  <Icon
                    loading={item.key === "draft" && isDrafting}
                    name={
                      item.key === "rewrite" ? "edit" :
                      item.key === "simplify" ? "align justify" :
                      item.key === "grammar" ? "spell check" :
                      item.key === "draft" ? "file text" :
                      item.key === "expand" ? "expand arrows alternate" : "magic"
                    }
                  />
                  <span>
                    {item.key === "draft" && isDrafting ? "Drafting..." : item.label}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal
        open={aiReviewModal.open}
        className={`ai-review-modal ${((aiReviewModal.action === "draft" || aiReviewModal.action === "generate" || aiReviewModal.action === "expand") && aiReviewModal.structuredSuggestion) ? "ai-review-modal-generate" : ""}`}
        size="large"
        onClose={closeAiReviewModal}
      >
        <div className="ai-review-modal-header">
          <h3>{ACTION_REVIEW_TITLES[aiReviewModal.action] || "AI Suggestion Review"}</h3>
          <p>Review the suggested change before applying it to the document.</p>
        </div>

        <div className="ai-review-modal-body" ref={modalBodyRef} style={{ position: 'relative' }}>
          {/* Draggable AI Warning Badge */}
          {aiReviewModal.groundingData?.is_general_ai && (
            <DraggableAiWarningBadge
              visible={true}
              text="AI generated — not source verified"
              containerRef={modalBodyRef}
            />
          )}
          
          {(aiReviewModal.action === "draft" || aiReviewModal.action === "generate" || aiReviewModal.action === "expand") && aiReviewModal.structuredSuggestion ? (
            <div className="ai-generate-review-layout">
              <div className="ai-generate-summary-card">
                <p className="ai-generate-tag">
                  {aiReviewModal.action === "expand"
                    ? "AI-expanded structured content"
                    : aiReviewModal.action === "draft"
                    ? "AI-drafted structured content"
                    : "AI-generated structured procedure"}
                </p>
                <h4>
                  {aiReviewModal.structuredSuggestion.procedureTitle || (aiReviewModal.action === "expand" ? "Expanded Content" : aiReviewModal.action === "draft" ? "Drafted Content" : "Generated Procedure")}
                </h4>
                <p>
                  {aiReviewModal.structuredSuggestion.procedureDescription ||
                    aiReviewModal.suggestedText}
                </p>
              </div>

              {aiReviewModal.structuredSuggestion.steps?.length > 0 && (
                <div className="ai-generate-steps-wrap">
                  <p className="ai-generate-steps-title">
                    Steps ({aiReviewModal.structuredSuggestion.steps.length})
                  </p>
                  <div className="ai-generate-steps-list">
                    {aiReviewModal.structuredSuggestion.steps.map((step, index) => (
                      <div
                        className="ai-generate-step-item"
                        key={`${step?.stepTitle || "step"}-${index}`}
                      >
                        <div className="ai-generate-step-index">{index + 1}</div>
                        <div>
                          <p className="ai-generate-step-heading">
                            {step?.stepTitle || `Step ${index + 1}`}
                          </p>
                          <p className="ai-generate-step-desc">
                            {step?.stepDescription || ""}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="ai-review-column ai-review-original">
                <h4>Original</h4>
                <div className="ai-review-content">{aiReviewModal.originalText}</div>
              </div>

              <div className="ai-review-column ai-review-suggestion">
                <h4>AI Suggestion</h4>
                <div className="ai-review-content">{aiReviewModal.suggestedText}</div>
              </div>
            </>
          )}
        </div>

        {/* Grounding Information Section */}
        {aiReviewModal.groundingData && (
          <div className="ai-grounding-section">
            {/* Source Verification Status - only show verified badge, not general AI warning */}
            {!aiReviewModal.groundingData.is_general_ai && aiReviewModal.groundingData.sources_used?.length > 0 && (
              <div className="ai-grounding-badge ai-badge-verified">
                <Icon name="check circle" />
                <span>Source Verified</span>
              </div>
            )}

            {/* Sources Used */}
            {aiReviewModal.groundingData.sources_used?.length > 0 && (
              <div className="ai-grounding-sources">
                <p className="ai-grounding-sources-title">
                  <Icon name="book" />
                  Sources Used ({aiReviewModal.groundingData.sources_used.length})
                </p>
                <div className="ai-grounding-sources-list">
                  {aiReviewModal.groundingData.sources_used.map((source, index) => (
                    <div key={index} className="ai-grounding-source-item">
                      <div className="ai-source-header">
                        <Icon name="file text outline" />
                        <span className="ai-source-title">
                          {source.procedure_title || source.title || source.document_id || `Source ${index + 1}`}
                        </span>
                      </div>
                      {source.chunks?.length > 0 && (
                        <div className="ai-source-chunks">
                          {source.chunks.slice(0, 3).map((chunk, chunkIdx) => (
                            <div key={chunkIdx} className="ai-source-chunk">
                              <span className="ai-chunk-text">
                                {chunk.text?.substring(0, 150) || chunk.content?.substring(0, 150) || ''}
                                {(chunk.text?.length > 150 || chunk.content?.length > 150) && '...'}
                              </span>
                            </div>
                          ))}
                          {source.chunks.length > 3 && (
                            <span className="ai-chunks-more">
                              +{source.chunks.length - 3} more chunks
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Needs Review Warning */}
            {aiReviewModal.groundingData.needs_review && (
              <div className="ai-grounding-warning">
                <Icon name="exclamation triangle" />
                <span>This content requires additional review before use.</span>
              </div>
            )}

            {/* Warnings */}
            {aiReviewModal.groundingData.warnings?.length > 0 && (
              <div className="ai-grounding-warnings">
                <p className="ai-grounding-warnings-title">
                  <Icon name="warning sign" />
                  Warnings
                </p>
                <ul className="ai-grounding-warnings-list">
                  {aiReviewModal.groundingData.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Input Conflicting Claims (draft/expand) */}
            {aiReviewModal.groundingData.input_conflicting_claims?.length > 0 && (
              <div className="ai-grounding-unsupported">
                <p className="ai-grounding-unsupported-title">
                  <Icon name="exclamation triangle" />
                  Review Claims ({aiReviewModal.groundingData.input_conflicting_claims.length})
                </p>
                <p className="ai-grounding-unsupported-subtitle">
                  Your input diverges from approved reference documents:
                </p>
                <ul className="ai-grounding-unsupported-list">
                  {aiReviewModal.groundingData.input_conflicting_claims
                    .slice(0, 10)
                    .map((claim, index) => (
                      <li key={index}>
                        {typeof claim === "string"
                          ? claim
                          : claim?.claim || claim?.text || JSON.stringify(claim)}
                      </li>
                    ))}
                  {aiReviewModal.groundingData.input_conflicting_claims.length > 10 && (
                    <li className="ai-unsupported-more">
                      +
                      {aiReviewModal.groundingData.input_conflicting_claims.length - 10}{" "}
                      more claims
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="ai-review-modal-footer">
          <button
            type="button"
            className="ai-review-btn ai-review-reject"
            onClick={closeAiReviewModal}
          >
            Reject
          </button>
          <button
            type="button"
            className="ai-review-btn ai-review-accept"
            onClick={applyReviewedSuggestion}
          >
            Accept & Apply
          </button>
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    aimodal: state.aimodal,
    featureFlag: state.featureFlag
  };
};

export default connect(mapStateToProps, {
    aiButtonon
})(FroalaEditor);
